import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { IndustriesCopyEditor } from "@/components/IndustriesCopyEditor";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

import { analyticsApi, siteCopyApi } from "@/services/phase1Api";
import { Save, ShieldAlert, Undo2 } from "lucide-react";

const safeJson = (v) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return "{}";
  }
};

export default function SiteCopySettings({ session, embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState({});

  const isAdmin = session?.user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await siteCopyApi.getAdmin(session);
        if (!mounted) return;
        setDoc(data);
        setDraft(data.content || {});
      } catch (e) {
        toast.error("Could not load site copy", {
          description: e?.response?.data?.detail || e?.message || "Try again.",
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isAdmin, session]);

  const dirty = useMemo(() => {
    return safeJson(draft) !== safeJson(doc?.content || {});
  }, [draft, doc]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <Card className="rounded-3xl border-border bg-foreground/5 p-6 text-foreground/75 backdrop-blur-2xl">
          <div className="flex items-center gap-2 text-foreground">
            <ShieldAlert className="h-4 w-4 text-[var(--brand)]" />
            Super Admin only
          </div>
          <p className="mt-3 text-sm text-foreground/60">
            This Settings page is restricted. Sign in as Super Admin to access it.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => navigate("/app")}
              className="h-11 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
            >
              Back to app
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "mx-auto max-w-6xl px-4 py-10 sm:px-6"}>
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          {!embedded && (
            <div className="text-xs uppercase tracking-wide text-foreground/55">
              Settings
            </div>
          )}
          <div className={embedded ? "text-lg font-semibold text-foreground" : "mt-2 text-2xl font-semibold text-foreground"}>
            Global Site Copy
          </div>
          <div className="mt-2 text-sm text-foreground/60">
            Edit the wording across the public site. Stored server-side (global)
            and applied everywhere.
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Badge className="rounded-full border-border bg-foreground/5 text-foreground/70">
            version {doc?.version || "—"}
          </Badge>
          <Button
            variant="secondary"
            disabled={!dirty || saving}
            onClick={() => setDraft(doc?.content || {})}
            className="h-11 rounded-none border border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
          >
            <Undo2 className="mr-2 h-4 w-4" />
            Revert
          </Button>
          <Button
            disabled={!dirty || saving}
            onClick={async () => {
              setSaving(true);
              try {
                const data = await siteCopyApi.update(draft, session);
                setDoc(data);
                setDraft(data.content || {});
                analyticsApi.track(
                  "site_copy_saved",
                  { version: data.version },
                  session,
                );
                toast.success("Saved", {
                  description: "Global copy updated.",
                });
              } catch (e) {
                toast.error("Could not save", {
                  description: e?.response?.data?.detail || e?.message || "Try again.",
                });
              } finally {
                setSaving(false);
              }
            }}
            className="h-11 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <Separator className="my-6 bg-foreground/10" />

      {loading ? (
        <Card className="rounded-3xl border-border bg-foreground/5 p-6 text-foreground/60 backdrop-blur-2xl">
          Loading…
        </Card>
      ) : (
        <Tabs defaultValue="hero">
          <TabsList className="rounded-none bg-muted flex-wrap">
            <TabsTrigger value="hero">Hero</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="orchestration">Orchestration</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="calendly">Calendly</TabsTrigger>
            <TabsTrigger value="industries">Industries</TabsTrigger>
            <TabsTrigger value="raw">Raw JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="hero" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Hero</div>
              <p className="mt-2 text-sm text-foreground/60">
                These keys are used by the homepage hero.
              </p>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Headline
                  </div>
                  <Input
                    value={draft.hero_headline || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, hero_headline: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Multiply productivity—without adding headcount."
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Subhead
                  </div>
                  <Textarea
                    value={draft.hero_subhead || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, hero_subhead: e.target.value }))
                    }
                    className="min-h-[44px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="We help Owners, CEOs, CFOs…"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Primary CTA label
                  </div>
                  <Input
                    value={draft.cta_primary || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, cta_primary: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Book Free 30-Minute Preliminary Assessment"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Secondary CTA label
                  </div>
                  <Input
                    value={draft.cta_secondary || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, cta_secondary: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Get My AI Opportunity Score"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Security Section</div>
              <p className="mt-2 text-sm text-foreground/60">
                Edit the security messaging displayed below the hero.
              </p>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Section Title
                  </div>
                  <Input
                    value={draft.security_title || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, security_title: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Security that makes customers happy"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Security Bullet Points (one per line)
                  </div>
                  <Textarea
                    value={(draft.security_items || []).join("\n")}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        security_items: e.target.value.split("\n").filter(x => x.trim())
                      }))
                    }
                    rows={6}
                    className="rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Staff can only access data you want them to see...&#10;See who has changed what...&#10;You control what is automated..."
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Highlight Box Text
                  </div>
                  <Input
                    value={draft.security_highlight || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, security_highlight: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="No automated production changes without written approval."
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="orchestration" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Agent Orchestration</div>
              <p className="mt-2 text-sm text-foreground/60">
                Edit the Agent Orchestration section content.
              </p>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Section Title
                  </div>
                  <Input
                    value={draft.agent_orch_title || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, agent_orch_title: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Agent Orchestration + Business Systems Integration"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Platform Badges (comma-separated)
                  </div>
                  <Input
                    value={(draft.agent_orch_platforms || []).join(", ")}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        agent_orch_platforms: e.target.value.split(",").map(x => x.trim()).filter(x => x)
                      }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="OpenClaw, Computer, Manas"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Intro Paragraph
                  </div>
                  <Textarea
                    value={draft.agent_orch_intro || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, agent_orch_intro: e.target.value }))
                    }
                    rows={4}
                    className="rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="The real power of AI isn't in chatbots..."
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Benefits Subtitle
                  </div>
                  <Input
                    value={draft.agent_orch_subtitle || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, agent_orch_subtitle: e.target.value }))
                    }
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="What that means for your business"
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Benefits (JSON array - advanced)
                  </div>
                  <Textarea
                    value={JSON.stringify(draft.agent_orch_benefits || [], null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setDraft((p) => ({ ...p, agent_orch_benefits: parsed }));
                      } catch {
                        // Invalid JSON, don't update
                      }
                    }}
                    rows={8}
                    className="rounded-none border-border bg-muted font-mono text-xs text-foreground placeholder:text-foreground/35"
                    placeholder='[{"title": "...", "desc": "...", "icon": "workflow"}]'
                  />
                  <p className="text-xs text-foreground/40">
                    Icons: workflow, plug, shield, eye, layers
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="pricing" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Pricing</div>
              <p className="mt-2 text-sm text-foreground/60">
                Optional overrides for the 4-step offer ladder.
              </p>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Pricing intro
                  </div>
                  <Textarea
                    value={draft.pricing_intro || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, pricing_intro: e.target.value }))
                    }
                    className="min-h-[44px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="A de-risked progression with transparent gates."
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    In-Depth Assessment tooltip
                  </div>
                  <Textarea
                    value={draft.detailed_tooltip || ""}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, detailed_tooltip: e.target.value }))
                    }
                    className="min-h-[60px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Priced by business size + workflow complexity…"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="calendly" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Calendly Integration</div>
              <p className="mt-2 text-sm text-foreground/60">
                Paste your Calendly scheduling link below. It will appear as an embedded widget on the Contact page.
              </p>

              <div className="mt-6 space-y-2">
                <div className="text-xs uppercase tracking-wide text-foreground/55">
                  Calendly URL
                </div>
                <Input
                  value={draft.calendly_url || ""}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, calendly_url: e.target.value }))
                  }
                  className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                  placeholder="https://calendly.com/your-name/30min"
                  data-testid="calendly-url-input"
                />
                <p className="text-xs text-foreground/40">
                  Example: https://calendly.com/your-name/qualification-call
                </p>
              </div>

              {draft.calendly_url && (
                <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm text-emerald-400">
                  Calendly widget will be embedded on the Contact page.
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="industries" className="mt-5">
            <IndustriesCopyEditor draft={draft} setDraft={setDraft} />
          </TabsContent>

          <TabsContent value="faq" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">FAQ</div>
              <p className="mt-2 text-sm text-foreground/60">
                For now, edit as raw JSON array.
              </p>

              <Textarea
                value={safeJson(draft.faq || [])}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setDraft((p) => ({ ...p, faq: parsed }));
                  } catch {
                    // ignore invalid JSON while typing
                  }
                }}
                className="mt-6 min-h-[220px] rounded-none border-border bg-muted font-mono text-xs text-foreground placeholder:text-foreground/35"
                placeholder='[{"q":"…","a":"…"}]'
              />
              <div className="mt-2 text-xs text-foreground/45">
                Tip: Use format: <span className="text-foreground">[{"{"}"q":"Question","a":"Answer"{"}"}]</span>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="raw" className="mt-5">
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground">Raw JSON</div>
              <p className="mt-2 text-sm text-foreground/60">
                Advanced: edit the full site-copy JSON. This is powerful but easy
                to break if you remove keys.
              </p>

              <Textarea
                value={safeJson(draft)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setDraft(parsed);
                  } catch {
                    // ignore invalid JSON while typing
                  }
                }}
                className="mt-6 min-h-[360px] rounded-none border-border bg-muted font-mono text-xs text-foreground"
              />
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <div className="mt-8 text-xs text-foreground/45">
        This is a CMS-lite global settings editor. Validation, per-section schemas, and version rollback are planned.
      </div>
    </div>
  );
}
